import React from "react";
import {
  Box,
  Chip,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  DATE_RANGE_PRESET_OPTIONS,
  DateRangePresetId,
} from "app/utils/dateRangePresets";
import { AssignmentFilter } from "app/utils/ordersBoard";
import type { Dayjs } from "dayjs";

type MobileOrdersFilterDrawerProps = {
  open: boolean;
  onClose: () => void;
  searchInput: string;
  onSearchChange: (value: string) => void;
  assignmentFilter: AssignmentFilter;
  onAssignmentFilterChange: (value: AssignmentFilter) => void;
  dateRangePreset: DateRangePresetId | "";
  onDateRangePresetChange: (preset: DateRangePresetId) => void;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  onFromDateChange: (value: Dayjs | null) => void;
  onToDateChange: (value: Dayjs | null) => void;
};

export function MobileOrdersFilterDrawer({
  open,
  onClose,
  searchInput,
  onSearchChange,
  assignmentFilter,
  onAssignmentFilterChange,
  dateRangePreset,
  onDateRangePresetChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
}: MobileOrdersFilterDrawerProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "88vh",
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 999,
            bgcolor: "#d1d5db",
            mx: "auto",
            mb: 2,
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          Filters
        </Typography>

        <TextField
          fullWidth
          size="small"
          label="Search order"
          value={searchInput}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Assignment</InputLabel>
          <Select
            label="Assignment"
            value={assignmentFilter}
            onChange={(event) =>
              onAssignmentFilterChange(event.target.value as AssignmentFilter)
            }
          >
            <MenuItem value="unassigned">Unassigned only</MenuItem>
            <MenuItem value="assigned">Assigned only</MenuItem>
            <MenuItem value="all">All orders</MenuItem>
          </Select>
        </FormControl>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Quick range</InputLabel>
          <Select
            label="Quick range"
            value={dateRangePreset}
            onChange={(event) =>
              onDateRangePresetChange(event.target.value as DateRangePresetId)
            }
          >
            {DATE_RANGE_PRESET_OPTIONS.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mb: 2 }}>
          <DatePicker
            label="From"
            value={fromDate}
            onChange={onFromDateChange}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
          <DatePicker
            label="To"
            value={toDate}
            onChange={onToDateChange}
            minDate={fromDate ?? undefined}
            slotProps={{ textField: { size: "small", fullWidth: true } }}
          />
        </Box>

        <Button
          fullWidth
          variant="contained"
          onClick={onClose}
          sx={{
            bgcolor: "#5fa67a",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { bgcolor: "#4d9164" },
          }}
        >
          Apply filters
        </Button>
      </Box>
    </Drawer>
  );
}

type MobileAreaTabsProps = {
  stages: Array<{ id: number | string; title: string; items: any[]; color?: string }>;
  selectedStageId: number | string | null;
  onSelectStage: (stageId: number | string) => void;
};

export function MobileAreaTabs({
  stages,
  selectedStageId,
  onSelectStage,
}: MobileAreaTabsProps) {
  return (
    <Box
      sx={{
        flexShrink: 0,
        display: "flex",
        gap: 1,
        overflowX: "auto",
        px: 1.5,
        py: 1,
        bgcolor: "#fff",
        borderBottom: "1px solid #e5e9ef",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {stages.map((stage) => {
        const isSelected =
          selectedStageId != null && String(stage.id) === String(selectedStageId);
        return (
          <Chip
            key={String(stage.id)}
            label={`${stage.title} (${stage.items.length})`}
            onClick={() => onSelectStage(stage.id)}
            sx={{
              flexShrink: 0,
              height: 36,
              fontWeight: 600,
              bgcolor: isSelected ? "#eef7f1" : "#f3f4f6",
              color: isSelected ? "#3d6b52" : "#4b5563",
              border: isSelected ? "1px solid #7ec597" : "1px solid transparent",
              "& .MuiChip-label": { px: 1.25 },
            }}
          />
        );
      })}
    </Box>
  );
}

type MobileMoveAreaDrawerProps = {
  open: boolean;
  stages: Array<{ id: number | string; title: string }>;
  currentStageId: number | string;
  onClose: () => void;
  onSelectArea: (stageId: number | string, stageTitle: string) => void;
};

export function MobileMoveAreaDrawer({
  open,
  stages,
  currentStageId,
  onClose,
  onSelectArea,
}: MobileMoveAreaDrawerProps) {
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: "70vh",
        },
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 3 }}>
        <Box
          sx={{
            width: 40,
            height: 4,
            borderRadius: 999,
            bgcolor: "#d1d5db",
            mx: "auto",
            mb: 2,
          }}
        />
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
          Move to delivery area
        </Typography>
        <Box sx={{ display: "grid", gap: 1, overflowY: "auto", maxHeight: "52vh" }}>
          {stages
            .filter((stage) => String(stage.id) !== String(currentStageId))
            .map((stage) => (
              <Button
                key={String(stage.id)}
                variant="outlined"
                onClick={() => onSelectArea(stage.id, stage.title)}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontWeight: 600,
                  color: "#374151",
                  borderColor: "#e5e9ef",
                }}
              >
                {stage.title}
              </Button>
            ))}
        </Box>
      </Box>
    </Drawer>
  );
}
